import React, { useEffect, useState, useRef } from "react";
import { Button, message, Form, Input, Upload, Modal, Progress, Select } from "antd";
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface";
import type { UploadChangeParam } from "antd/es/upload";
import {
  LoadingOutlined,
  PlusSquareFilled,
  CloudUploadOutlined,
  CloseCircleFilled,
  CheckCircleFilled,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { cloneDeep, delay, debounce } from "lodash-es";
import { useSelector } from "react-redux";
import api from "@/api";
import apiStaging from "@/api/staging";
import { format, findDeep, changeSize, getDevicePluginDetail, resetFormValidate, validateFormField } from "@/utils";
import { appNameReg, devicePluginPackageNameReg, devicePluginVersionNameReg } from "@/utils/constant";
import { uploadBaseUrl } from "@/api/release";
import ApplyDown from "./ApplyDown";

import dataJson from "@/../public/data.json";

const prefix = "myDevicePluginDetail-version_manage";
import "./index.less";
import { getAuthTokenValue } from "@kaihong/utils/lib/khAuthToken";

const VersionManage: React.FC = () => {
  const { type, id } = useParams();
  const {
    release,
    please_enter,
    icon_text,
    one_bars,
    two_bars,
    please_upload,
    save_text,
    save_success_text,
    req_err,
    cancelText,
    confirmText,
    my_device_plugin_detail,
    asterisk,
    description_label,
    package_name_label,
    my_ai_model,
  } = dataJson;
  const [iconLoading, setIconLoading] = useState(false);
  const [iconImageUrl, setIconImageUrl] = useState<string>();
  const detailData = useRef<any>();

  const [packageData, setPackageData] = useState<any>();
  const [packageLoading, setPackageLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const [savaLoading, setSavaLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [cancelReleaseLoading, setCancelReleaseLoading] = useState(false);
  const [cancelDownLoading, setCancelDownLoading] = useState(false);

  const [applyDownVisible, setApplyDownVisible] = useState(false);

  const devicePluginDetail = useSelector((state: any) => state.devicePluginDetail);

  const [baseForm] = Form.useForm();
  const baseFormValues = Form.useWatch([], baseForm);

  const uploadUrl = `${uploadBaseUrl}/app/upload/public/${id}`;

  const renderBtn = (item: string) => {
    const newShowMode =
      detailData.current?.histories?.length > 1 && detailData.current?.histories[0]?.showMode === "AUDIT_UPGRADE";
    switch (item) {
      case "save":
        return (
          <Button type="primary" loading={savaLoading} ghost style={{ marginLeft: "16px" }} onClick={handleSave}>
            {save_text}
          </Button>
        );
      case "submitReview":
        return (
          <Button type="primary" loading={submitLoading} style={{ marginLeft: "16px" }} onClick={submitReview}>
            {release.submit_review}
          </Button>
        );
      case "cancelRelease":
        return (
          <Button type="primary" style={{ marginLeft: "16px" }} onClick={cancelRelease}>
            {release.cancel_release}
          </Button>
        );
      case "down":
        if (newShowMode) {
          break;
        }
        return (
          <Button
            type="primary"
            ghost
            style={{ marginLeft: "16px" }}
            onClick={() => {
              setApplyDownVisible(true);
            }}
          >
            {release.audit_down}
          </Button>
        );
      case "cancelDown":
        return (
          <Button type="primary" style={{ marginLeft: "16px" }} onClick={cancelDown}>
            {release.cancel_down}
          </Button>
        );
      default:
        return;
    }
  };

  const validatePackageName = (_: any, value: any) => {
    if (value && devicePluginPackageNameReg.test(value)) {
      return api.existDevicePluginPackageName({ packageName: value, id }).then((resp: any) => {
        if (resp.code === 200) {
          return Promise.resolve();
        } else {
          return Promise.reject(new Error(resp.msg));
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const validateVersion = (_: any, value: any) => {
    if (value && devicePluginVersionNameReg.test(value)) {
      return api.existDevicePluginVersion({ version: value, id }).then((resp: any) => {
        if (resp.code === 200) {
          return Promise.resolve();
        } else {
          return Promise.reject(new Error(resp.msg));
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const validateName = (_: any, value: any) => {
    if (value) {
      return apiStaging.allRepeatName({ name: value, id }).then((resp: any) => {
        if (resp.code === 200) {
          if (resp.data?.exist) {
            return Promise.reject(my_ai_model.name_already);
          }
        } else {
          return Promise.resolve();
        }
      });
    } else {
      return Promise.resolve();
    }
  };

  const scrollErrDom = (domClass: string, delayTime?: number) => {
    if (delayTime) {
      delay(() => {
        const errorList = (document as any).querySelectorAll(domClass);
        //由于校验失败ant会自动给失败表单项添加类名，直接获取即可
        errorList[0]?.scrollIntoView({
          block: "center",
          behavior: "smooth",
        });
      }, delayTime);
    } else {
      const errorList = (document as any).querySelectorAll(domClass);
      //由于校验失败ant会自动给失败表单项添加类名，直接获取即可
      errorList[0]?.scrollIntoView({
        block: "center",
        behavior: "smooth",
      });
    }
  };

  // 处理请求参数
  const handleReq = async (isValidate: boolean) => {
    if (iconLoading) {
      message.warning(release.upload_img_ing);
      return;
    } else if (packageLoading) {
      message.warning(release.upload_package_ing);
      return;
    }

    let baseValues: any;
    if (isValidate) {
      try {
        baseValues = await baseForm.validateFields();
      } catch (error) {
        scrollErrDom(".ant-form-item-has-error", 100);
        return;
      }
    } else {
      if (
        baseFormValues.name == undefined ||
        baseFormValues.name == null ||
        baseFormValues.name == "" ||
        (!(baseFormValues.name == undefined || baseFormValues.name == null || baseFormValues.name == "") &&
          (await validateFormField({ form: baseForm, name: "name" })))
      ) {
        resetFormValidate([{ form: baseForm, formValues: baseFormValues }]);
        baseForm.validateFields(["name"]);
        scrollErrDom(".ant-form-item-has-error", 300);
        return;
      }
      // 包号校验
      if (
        !(
          baseFormValues.packageName == undefined ||
          baseFormValues.packageName == null ||
          baseFormValues.packageName == ""
        ) &&
        (await validateFormField({ form: baseForm, name: "packageName" }))
      ) {
        await resetFormValidate([{ form: baseForm, formValues: baseFormValues }]);
        baseForm.validateFields(["packageName"]);
        scrollErrDom(".ant-form-item-has-error", 300);
        return;
      }
      // 版本号校验
      if (
        baseFormValues.version == undefined ||
        baseFormValues.version == null ||
        baseFormValues.version == "" ||
        (!(baseFormValues.version == undefined || baseFormValues.version == null || baseFormValues.version == "") &&
          (await validateFormField({ form: baseForm, name: "version" })))
      ) {
        await resetFormValidate([{ form: baseForm, formValues: baseFormValues }]);
        baseForm.validateFields(["version"]);
        scrollErrDom(".ant-form-item-has-error", 300);
        return;
      }
      baseValues = await baseForm.getFieldsValue();
    }

    try {
      const resourcesArr = [];
      const iconIds = baseValues?.iconResources;
      !!iconIds &&
        resourcesArr.push({
          resourceIds: [iconIds],
          type: "ICON",
        });

      let supportProtocol = [];
      if (baseValues?.supportProtocol) {
        supportProtocol = Array.isArray(baseValues?.supportProtocol)
          ? baseValues?.supportProtocol
          : [baseValues?.supportProtocol];
      }
      const req = {
        description: baseValues?.description,
        id: id,
        name: baseValues?.name,
        packageName: baseValues?.packageName,
        introduction: baseValues?.introduction,
        instanceIds: baseValues?.instanceIds?.instanceId ? [baseValues?.instanceIds?.instanceId] : [],
        resources: resourcesArr,
        supportProtocol,
        relationProduct: baseValues?.relationProduct,
        version: baseValues?.version,
        abilityName: baseValues?.abilityName,
        packageType: baseValues?.packageType,
      };
      return req;
    } catch (errorInfo: any) {
      console.error(errorInfo);
      return;
    }
  };

  const handleSave = async () => {
    try {
      setSavaLoading(true);
      const req: any = await handleReq(false);
      if (req) {
        try {
          // if (!req?.instanceIds?.length) {
          //   baseForm.setFieldValue("version", "");
          //   req.version = "";
          // }
          const res: any = await api.saveDevicePlugin(req);
          if (res?.code == 200) {
            getDevicePluginDetail(id);
            message.success(save_success_text);
          } else {
            message.error(res.msg);
          }
        } catch (errorInfo: any) {
          message.error(errorInfo?.response?.data?.msg || req_err);
        }
      }
      setSavaLoading(false);
    } catch (error) {
      setSavaLoading(false);
      console.log(error);
    }
  };

  // 提交审核
  const submitReview = debounce(async () => {
    const req = await handleReq(true);

    if (req) {
      try {
        setSubmitLoading(true);
        const res: any = await api.saveNextDevicePlugin(req);
        setSubmitLoading(false);
        if (res?.code == 200) {
          // 接口要区分是否是升级的提交
          let res2: any = null;
          if (detailData.current?.isUpgrade) {
            res2 = await api.auditUpgradeDevicePlugin({ id });
          } else {
            res2 = await api.auditReleaseDevicePlugin({ id });
          }
          if (res2?.code == 200) {
            message.success(release.wait_review);
            getDevicePluginDetail(id);
          } else {
            message.error(res2.msg);
          }
        } else {
          message.error(res.msg);
        }
      } catch (errorInfo: any) {
        setSubmitLoading(false);
        message.error(errorInfo?.response?.data?.msg || req_err);
      }
    }
  }, 300);

  const cancelRelease = async () => {
    const modal = Modal.confirm({
      icon: null,
      centered: true,
      closable: true,
      width: 350,
      title: release.cancel_release_title,
      content: release.cancel_release_content,
      footer: (
        <div style={{ textAlign: "right", marginTop: "20px" }}>
          <Button
            type="primary"
            ghost
            onClick={() => {
              modal.destroy();
            }}
          >
            {cancelText}
          </Button>
          <Button
            type="primary"
            style={{ marginLeft: "24px" }}
            loading={cancelReleaseLoading}
            onClick={async () => {
              try {
                setCancelReleaseLoading(true);
                let res: any = null;
                if (detailData.current?.isUpgrade) {
                  res = await api.cancelUpgradeDevicePlugin({ id });
                } else {
                  res = await api.cancelReleaseDevicePlugin({ id });
                }
                setCancelReleaseLoading(false);
                if (res?.code == 200) {
                  message.success(release.cancel_release_success);
                  getDevicePluginDetail(id);
                  modal.destroy();
                } else {
                  message.error(res.msg);
                }
              } catch (errorInfo: any) {
                setCancelReleaseLoading(false);
                message.error(errorInfo?.response?.data?.msg || req_err);
              }
            }}
          >
            {confirmText}
          </Button>
        </div>
      ),
    });
  };

  const cancelDown = async () => {
    const modal = Modal.confirm({
      icon: null,
      centered: true,
      closable: true,
      width: 350,
      title: release.cancel_down,
      content: release.cancel_down_content,
      footer: (
        <div style={{ textAlign: "right", marginTop: "20px" }}>
          <Button
            type="primary"
            ghost
            onClick={() => {
              modal.destroy();
            }}
          >
            {cancelText}
          </Button>
          <Button
            type="primary"
            loading={cancelDownLoading}
            style={{ marginLeft: "24px" }}
            onClick={async () => {
              try {
                setCancelDownLoading(true);
                const res: any = await api.cancelDownDevicePlugin({ id: id });
                setCancelDownLoading(false);
                if (res?.code == 200) {
                  message.success(release.cancel_down_success);
                  getDevicePluginDetail(id);
                  modal.destroy();
                } else {
                  message.error(res.msg);
                }
              } catch (errorInfo: any) {
                setCancelDownLoading(false);
                message.error(errorInfo?.response?.data?.msg || req_err);
              }
            }}
          >
            {confirmText}
          </Button>
        </div>
      ),
    });
  };

  const applyDownClose = () => {
    setApplyDownVisible(false);
    getDevicePluginDetail(id);
  };

  // 初始化数据
  const initHandle = async () => {
    const dataCopy = cloneDeep(detailData.current);

    let packObj = null;
    packObj = dataCopy?.appInstances?.length
      ? {
          ...dataCopy?.appInstances[0]?.packageResource,
          version: dataCopy?.appInstances[0]?.version,
          instanceId: dataCopy?.appInstances[0]?.instanceId,
        }
      : null;

    const iconItem = dataCopy?.resources?.find((item: any) => item.type == "ICON");

    const relationProductIds = dataCopy?.relationProductDataList?.map((item: any) => {
      return item?.deleted ? item?.productName : item?.productId;
    });
    const productFilterDelIds = dataCopy?.relationProductDataList
      ?.filter((item: any) => !item?.deleted)
      ?.map((item: any) => item.productId);

    baseForm.setFieldsValue({
      name: dataCopy?.name || "",
      introduction: dataCopy?.introduction || "",
      description: dataCopy?.description || "",
      iconResources: iconItem?.id || "",
      supportProtocol: dataCopy?.supportProtocol,
      packageType: dataCopy?.packageType || my_device_plugin_detail.package_format_options[0].value,
      packageName: dataCopy?.packageName,
      abilityName: dataCopy?.abilityName,
      instanceIds: packObj,
      version: dataCopy?.version,
      relationProduct: detailData.current?.disabled ? relationProductIds : productFilterDelIds,
    });
    setPackageData(packObj);
    setIconImageUrl(iconItem?.url);
  };

  // 图片上传前处理
  const beforeUpload: any = (file: RcFile, { width, height, size, accept = ["png"] }: any) => {
    return new Promise<void>((resolve, reject) => {
      const isAccept = accept.some((item: string) => file.type === `image/${item}`);
      const acceptText = accept.join("、").toUpperCase();

      if (!isAccept) {
        message.error(
          `${release.upload_accept_err.text1}${width}${asterisk}${height}${release.upload_accept_err.text2}${acceptText}${release.upload_accept_err.text3}`
        );
        reject();
        return;
      }
      if (size) {
        const imgSize = file.size;
        if (imgSize > size) {
          const sizeText = changeSize(size);
          message.error(`${release.upload_size_err.text1}${sizeText}${release.upload_size_err.text2}`);

          reject();
          return;
        }
      }
      const _URL = window.URL || window.webkitURL;
      const img = new Image();
      img.src = _URL.createObjectURL(file);

      img.onerror = () => {
        message.error(release.upload_fail);
      };
      img.onload = () => {
        if (img.width != width || img.height != height) {
          message.error(
            `${release.upload_accept_err.text1}${width}${asterisk}${height}${release.upload_accept_err.text2}${acceptText}${release.upload_accept_err.text3}`
          );

          reject();
        } else {
          resolve();
        }
      };
    });
  };

  // 图标新增
  const iconImageChange: UploadProps["onChange"] = (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === "uploading") {
      setIconLoading(true);
      return;
    } else {
      setIconLoading(false);
    }
    if (info.file.status === "done") {
      if (info.file.response.code === 200) {
        const iconUrl = info?.file?.response?.data?.url;
        const iconId = info?.file?.response?.data?.id;

        setIconLoading(false);
        setIconImageUrl(iconUrl);
        baseForm.setFieldValue("iconResources", iconId);
      } else {
        message.error(info.file.response.msg);
      }
    } else {
      if (!iconImageUrl?.length) {
        baseForm.setFieldValue("iconResources", "");
        setIconImageUrl("");
      }
    }
    baseForm.validateFields(["iconResources"]);
  };

  // 软件包上传前处理
  const packageBeforeUpload = (file: RcFile) => {
    const packageFormatUpload: any = my_device_plugin_detail.package_format_upload;
    const uploadType =
      packageFormatUpload[baseFormValues?.packageType || my_device_plugin_detail.package_format_options[0].value];
    return new Promise<void>((resolve, reject) => {
      const isSizeOk = file.size <= uploadType.packageMaxSize;
      const fileName = file.name?.split(".");
      const isFileTypeOk = uploadType.packageNameEnd.some((item: string) => {
        return item.includes(fileName[fileName?.length - 1]);
      });

      if (!isFileTypeOk) {
        message.error(uploadType.packageTypeErr);
        reject();
        return;
      }

      if (!isSizeOk) {
        message.error(uploadType.packageSizeErr);
        reject();
        return;
      }

      resolve();
    });
  };
  // 上传软件包
  const packageReq = async (options: any) => {
    setPackageLoading(true);
    const { file } = options;

    setPackageData({ ...packageData, name: file.name });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("appId", id!);
    formData.append("type", "SO");
    formData.append("subType", "SO");
    try {
      api
        .uploadFile(formData, id, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          // 上传进度回调
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent?.progress) {
              const progressNumber = Math.floor(progressEvent?.progress * 100);
              setUploadProgress(progressNumber);
            }
          },
          errorIntercept: true,
        })
        .then((res: any) => {
          if (res.code !== 200) {
            message.error(res.msg);
            baseForm.setFieldValue("instanceIds", null);
          } else {
            setPackageData(res.data);
            baseForm.setFieldValue("instanceIds", res.data);
          }
        })
        .catch((err: any) => {
          message.error(err?.response?.data?.msg || release.upload_fail);
        })
        .finally(() => {
          setPackageLoading(false);
          baseForm.validateFields(["instanceIds"]);
          setUploadProgress(0);
        });
    } catch (err) {
      console.error(err);
      setPackageLoading(false);
    }
  };
  // 删除软件包
  const packageDel = () => {
    setPackageData(null);
    baseForm.setFieldValue("instanceIds", null);
    baseForm.validateFields(["instanceIds"]);
  };

  const getOptions = async (type: string) => {
    try {
      const res: any = await api.getNewDict(type);
      if (res?.code == 200) {
        return res?.data?.[type] || [];
      } else {
        message.error(res.msg);
        return [];
      }
    } catch (errorInfo: any) {
      message.error(errorInfo?.response?.data?.msg || req_err);
      return [];
    }
  };

  const [supportProtocolOption, setSupportProtocolOption] = useState<any[]>([]);
  const [relationProductOption, setRelationProductOption] = useState<any[]>([]);
  const domainAndTopic = async () => {
    const { supportProtocol } = my_device_plugin_detail.dict;
    const supportProtocolList: any = await getOptions(supportProtocol);
    setSupportProtocolOption(supportProtocolList);

    const res: any = await api.getProductManageNoPage({});
    if (res.code === 200) {
      setRelationProductOption(res.data);
    }
  };

  useEffect(() => {
    if (devicePluginDetail) {
      const resData = cloneDeep(devicePluginDetail);

      const resShowMode = findDeep(release.show_mode_arr, resData?.showMode);
      const nowIndex = resData?.histories.findIndex((item: any) => item.id == resData.id);
      if (nowIndex === 0) {
        //最新版本
        if (resShowMode?.isUpgrade && resData?.histories?.length > 1) {
          // 有升级场景，在新版本页面，按钮要受到旧版本状态影响
          const oldShowMode = resData?.histories[1]?.showMode;
          resData.baseBtns = resShowMode?.btnsByOld[oldShowMode];
        } else {
          resData.baseBtns = resShowMode?.baseBtns;
        }
      } else if (nowIndex === 1) {
        //旧版本
        if (resShowMode?.btnsByNew && resData?.histories?.length > 1) {
          // 有升级场景，在旧版本页面，按钮要受到新版本状态影响
          resData.baseBtns = resShowMode?.btnsByNew[resShowMode];
        } else {
          resData.baseBtns = resShowMode?.baseBtns;
        }
      } else {
        // 历史版本
        resData.baseBtns = resShowMode?.baseBtns;
      }

      resData.showModeLabel = resShowMode?.title;
      resData.isUpgrade = !!resShowMode?.isUpgrade;

      if (resData?.appInstances[0]?.version) {
        resData.showModeLabel = resData?.appInstances[0]?.version + one_bars + resShowMode?.title;
      } else {
        resData.showModeLabel = my_device_plugin_detail.new_version + one_bars + resShowMode?.title;
      }
      // 当存在两个版本时候，旧版本都不可修改
      // 准备提交
      if (resShowMode.value === "UPGRADING") {
        resData.disabled = false;
      } else if (resData?.histories?.length > 1 && resData?.histories[1]?.id === id) {
        resData.disabled = true;
      } else {
        resData.disabled = resShowMode?.formDisabled;
      }

      resData.packageDisabled =
        resShowMode?.packageDisabled || (resShowMode?.modeObj && resShowMode?.modeObj[resData.mode]?.packageDisabled);
      resData.allowChangePackageList =
        resShowMode?.allowChangePackageList ||
        (resShowMode?.modeObj && resShowMode?.modeObj[resData.mode]?.allowChangePackageList);

      resData.opinion = resShowMode?.opinion;

      resData.titleDescriptionLabel = resShowMode?.titleDescription
        ? resShowMode?.pleaseWrite
          ? resShowMode?.pleaseWrite + my_device_plugin_detail.detail_title + resShowMode?.titleDescription
          : my_device_plugin_detail.detail_title + resShowMode?.titleDescription
        : "";

      detailData.current = resData;
      initHandle();
    }
  }, [devicePluginDetail]);

  useEffect(() => {
    getDevicePluginDetail(id);
    domainAndTopic();
  }, []);

  return (
    <div className={`appDetail-content ${prefix}`}>
      <div className={`${prefix}-header`}>
        <div className={`${prefix}-header-left`}>
          <div title={detailData.current?.showModeLabel} className="one-line-ellipsis">
            {detailData.current?.showModeLabel}
          </div>
          {detailData.current?.titleDescriptionLabel ? <div>{detailData.current?.titleDescriptionLabel}</div> : null}
          <div className="one-line-ellipsis" title={detailData.current?.opinion || detailData.current?.audit?.content}>
            <span>{release.review_comments}</span>
            {detailData.current?.opinion || detailData.current?.audit?.content || two_bars}
          </div>
        </div>
        <div>
          {detailData.current?.baseBtns?.map((item: string) => {
            return <span key={item}>{renderBtn(item)}</span>;
          })}
        </div>
      </div>
      <div className={`${prefix}-content`}>
        <Form
          disabled={detailData.current?.disabled}
          form={baseForm}
          layout="vertical"
          scrollToFirstError={true}
          className={`${prefix}-baseForm ${prefix}-form`}
        >
          <Form.Item
            label={release.name_label}
            name="name"
            validateDebounce={500}
            rules={[
              { required: true, message: my_device_plugin_detail.name_placeholder },
              {
                pattern: appNameReg,
                message: my_device_plugin_detail.name_validator,
              },
              { validator: validateName },
            ]}
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
          >
            <Input
              placeholder={`${my_device_plugin_detail.name_placeholder}`}
              showCount
              maxLength={30}
              style={{ width: 524 }}
            />
          </Form.Item>
          <Form.Item
            label={my_device_plugin_detail.support_agreement}
            name="supportProtocol"
            validateDebounce={500}
            rules={[
              { required: true, message: my_device_plugin_detail.support_agreement_placeholder },
              {
                pattern: appNameReg,
                message: my_device_plugin_detail.name_validator,
              },
            ]}
          >
            <Select
              // mode="multiple"
              options={supportProtocolOption}
              placeholder={my_device_plugin_detail.support_agreement_placeholder}
              showSearch
              fieldNames={{ label: "dictLabel", value: "dictCode" }}
              filterOption={(input, option) => (option?.dictLabel ?? "").toLowerCase().includes(input.toLowerCase())}
              style={{ width: 524 }}
            />
          </Form.Item>
          <Form.Item
            label={my_device_plugin_detail.package_format}
            name="packageType"
            validateDebounce={500}
            rules={[{ required: true, message: my_device_plugin_detail.package_format_placeholder }]}
          >
            <Select
              options={my_device_plugin_detail.package_format_options}
              placeholder={my_device_plugin_detail.support_agreement_placeholder}
              showSearch
              style={{ width: 524 }}
              disabled={!detailData.current?.packageTypeEditable}
              onChange={() => {
                baseForm.setFieldValue("abilityName", "");
                packageDel();
              }}
            />
          </Form.Item>
          <Form.Item
            label={package_name_label}
            name="packageName"
            validateDebounce={500}
            rules={[
              {
                required: baseFormValues?.packageType === my_device_plugin_detail.package_format_options[1].value,
                message: my_device_plugin_detail.package_name_placeholder,
              },
              {
                pattern: devicePluginPackageNameReg,
                message: my_device_plugin_detail.package_validator,
              },
              {
                validator: validatePackageName,
              },
            ]}
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
          >
            <Input
              placeholder={`${my_device_plugin_detail.package_name_placeholder}`}
              showCount
              maxLength={127}
              style={{ width: 524 }}
            />
          </Form.Item>
          {baseFormValues?.packageType === my_device_plugin_detail.package_format_options[1].value && (
            <Form.Item
              label={my_device_plugin_detail.ablity}
              name="abilityName"
              validateDebounce={500}
              rules={[{ required: true, message: my_device_plugin_detail.ablity_placeholder }]}
              getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
            >
              <Input
                placeholder={`${my_device_plugin_detail.ablity_placeholder}`}
                showCount
                maxLength={127}
                style={{ width: 524 }}
              />
            </Form.Item>
          )}

          <Form.Item
            label={release.introduction}
            name="introduction"
            validateDebounce={500}
            rules={[{ required: true, message: release.introduction_required }]}
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
          >
            <Input placeholder={release.introduction_required} showCount maxLength={30} style={{ width: 524 }} />
          </Form.Item>

          <Form.Item
            label={release.description_label}
            name="description"
            rules={[
              {
                required: true,
                message: `${please_enter}${description_label}`,
              },
            ]}
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
          >
            <Input.TextArea
              placeholder={`${please_enter}${description_label}`}
              showCount
              maxLength={300}
              autoSize={{ minRows: 4, maxRows: 6 }}
              style={{ width: 524 }}
            />
          </Form.Item>
          <Form.Item
            label={release.icon_label}
            name="iconResources"
            rules={[{ required: true, message: `${please_upload}${my_device_plugin_detail.detail_title}${icon_text}` }]}
            className={`${prefix}-baseForm-iconResources`}
          >
            <div>
              <div className={`${prefix}-form-tip`}>{release.icon_tip}</div>
              <Upload
                accept="image/*"
                listType="picture-card"
                showUploadList={false}
                disabled={detailData.current?.disabled || iconLoading}
                maxCount={1}
                action={uploadUrl}
                headers={{
                  Authorization: getAuthTokenValue("Authorization") || "",
                }}
                data={{
                  appId: id,
                  type: "ICON",
                  subType: "ICON",
                }}
                beforeUpload={async (e) => {
                  await beforeUpload(e, { width: 256, height: 256, size: 3145728, accept: ["png"] });
                }}
                onChange={iconImageChange}
                style={{ margin: 0 }}
              >
                {iconImageUrl ? (
                  <img src={iconImageUrl} alt="avatar" />
                ) : iconLoading ? (
                  <LoadingOutlined />
                ) : (
                  <PlusSquareFilled className={`${prefix}-add-icon`} />
                )}
              </Upload>
            </div>
          </Form.Item>

          <Form.Item
            label={release.package_label}
            name="instanceIds"
            rules={[{ required: true, message: release.package_required }]}
            className={`${prefix}-versionForm-packageResources`}
          >
            <div>
              <div className={`${prefix}-form-tip`}>
                {
                  (my_device_plugin_detail.package_format_upload as Record<string, { packageTip: string }>)?.[
                    baseFormValues?.packageType || my_device_plugin_detail.package_format_options[0].value
                  ]?.packageTip
                }
              </div>
              {packageData?.resourceId ? (
                <div className={`${prefix}-package-data`} style={{ width: 524 }}>
                  <span title={packageData?.name} className="one-line-ellipsis">
                    {packageData?.name}
                  </span>
                  <span title={packageData?.version} className="one-line-ellipsis">
                    {packageData?.version}
                  </span>
                  <span>{format(new Date(Number(packageData?.createTime)), "yyyy/MM/dd-HH:mm:ss")}</span>
                  <span>{changeSize(packageData?.length)}</span>
                  {detailData.current?.disabled || detailData.current?.packageDisabled ? null : (
                    <CloseCircleFilled
                      style={{ color: "#000", cursor: "pointer" }}
                      className={`${prefix}-package-del`}
                      onClick={packageDel}
                    />
                  )}
                </div>
              ) : packageLoading ? (
                <div className={`${prefix}-package-loading`} style={{ width: 524 }}>
                  <span title={packageData?.name} className="one-line-ellipsis">
                    {packageData?.name}
                  </span>
                  {uploadProgress < 100 ? (
                    <span>
                      {release.uploading_label}
                      <Progress type="circle" size={30} percent={uploadProgress} />
                    </span>
                  ) : (
                    <span>
                      {release.analysising_label}
                      <LoadingOutlined style={{ color: "#0A59F7", fontSize: 30 }} />
                    </span>
                  )}
                </div>
              ) : (
                <Upload
                  beforeUpload={packageBeforeUpload}
                  customRequest={packageReq}
                  showUploadList={false}
                  style={{ margin: 0 }}
                >
                  <div className={`${prefix}-package-add`}>
                    <CloudUploadOutlined className={`${prefix}-package-cloud`} />
                    <PlusSquareFilled className={`${prefix}-package-plus`} />
                  </div>
                </Upload>
              )}
            </div>
          </Form.Item>
          <Form.Item
            label={my_device_plugin_detail.package_version}
            name="version"
            validateDebounce={500}
            rules={[
              { required: true, message: my_device_plugin_detail.package_version_placeholder },
              {
                pattern: devicePluginVersionNameReg,
                message: my_device_plugin_detail.version_validator,
              },
              {
                validator: validateVersion,
              },
            ]}
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, "")}
          >
            <Input
              placeholder={`${my_device_plugin_detail.package_version_placeholder}`}
              style={{ width: 524 }}
              showCount
              maxLength={30}
            />
          </Form.Item>
          <Form.Item
            label={my_device_plugin_detail.related_product}
            name="relationProduct"
            rules={[{ required: true, message: my_device_plugin_detail.related_product_placeholder }]}
          >
            <Select
              mode="multiple"
              options={relationProductOption}
              placeholder={my_device_plugin_detail.related_product_placeholder}
              showSearch
              fieldNames={{ label: "name", value: "id" }}
              filterOption={(input, option) => (option?.name ?? "").toLowerCase().includes(input.toLowerCase())}
              style={{ width: 524 }}
            />
          </Form.Item>
        </Form>
      </div>
      <ApplyDown type={type} id={id} visible={applyDownVisible} close={applyDownClose} />
    </div>
  );
};

export default VersionManage;
